import { api } from '@/lib/api';
import type { IndividualIntensiveClass } from '@lentera/shared';

export interface Summary {
  tbmTotal: number;
  tbmAssessed: number;
  tbmPending: number;
  meanIndex: number | null;
  bands: { name: string; count: number }[];
  regions: { name: string; mean: number; count: number }[];
  ranking: {
    tbmId: string;
    code: string;
    name: string;
    index: number | null;
    band: string | null;
    programYear: number | null;
  }[];
  findings: { open: number; inProgress: number; resolved: number };
}

export interface DimensionResult {
  dimensionId: string;
  code: string;
  name: string;
  weight: number;
  value: number;
  answeredCount: number;
  totalCount: number;
}

export interface TrendDimension {
  dimensionId: string;
  code: string;
  name: string;
  weight: number;
  value: number;
  percentage: number;
}

export interface TrendItem {
  periodName: string;
  semester: number;
  index: number | null;
  band: string | null;
  programYear: number | null;
  dimensions?: TrendDimension[];
}

export interface TbmReport {
  tbm: {
    id: string;
    code: string;
    name: string;
    address?: string | null;
    personInCharge?: string | null;
    phone?: string | null;
    angkatan?: string | null;
    regionPath?: { id: string; name: string; level: string }[];
  };
  bands?: { gt: number | null; name: string; display?: string; nameIndividual?: string | null; characteristic?: string | null }[];
  latest: {
    id: string;
    periodName: string;
    index: number | null;
    band: string | null;
    programYear: number | null;
    status: string;
  } | null;
  trend: TrendItem[];
  dimensions: DimensionResult[];
  intensiveClass?: IndividualIntensiveClass | null;
}

export interface IndividualReport {
  individual: {
    id: string;
    code: string;
    name: string;
    gender: 'L' | 'P' | null;
    profession?: string | null;
    personalBranding?: string | null;
    birthPlace?: string | null;
    birthDate?: string | null;
    phone?: string | null;
    email?: string | null;
    lastEducation?: string | null;
    photoKey?: string | null;
    tbmName?: string | null;
    tbmCode?: string | null;
    regionPath?: { id: string; name: string; level: string }[];
  };
  bands?: { gt: number | null; name: string; display?: string; nameIndividual?: string | null; characteristic?: string | null }[];
  latest: {
    id: string;
    periodName: string;
    index: number | null;
    band: string | null;
    programYear: number | null;
    status: string;
  } | null;
  trend: TrendItem[];
  dimensions: DimensionResult[];
  intensiveClass?: IndividualIntensiveClass | null;
}

export const analyticsApi = {
  summary: (periodId?: string) =>
    api.get<Summary>(`/analytics/summary${periodId ? `?periodId=${periodId}` : ''}`),
  tbmReport: (id: string) => api.get<TbmReport>(`/analytics/tbm/${id}`),
  individualReport: (id: string) => api.get<IndividualReport>(`/analytics/individual/${id}`),
  trend: () =>
    api.get<{
      periods: { semester: number; name: string; mean: number; count: number }[];
      series: {
        semester: number;
        periodName: string;
        index: number | null;
        tbmId?: string;
        tbmCode: string;
        tbmName?: string;
      }[];
    }>('/analytics/trend'),
};
